// src/pages/CitasPage.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../axiosInstance';
import AddCita from '../components/AddCita';
import CitaCalendar from '../components/CitaCalendar';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Paper,
  useMediaQuery,
  CircularProgress,
  Alert,
  Fade,
  Avatar,
  Divider,
  IconButton,
  Card,
  CardContent,
  Chip,
  Grid,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ButtonGroup
} from '@mui/material';
import { toast } from 'react-toastify';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EmailIcon from '@mui/icons-material/Email';
import TouchAppIcon from '@mui/icons-material/TouchApp';
// NUEVO: iconos para los cambios de vista en mobile
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import TodayIcon from '@mui/icons-material/Today';
import { motion } from 'framer-motion';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';

const CitasPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { hasRecordatoriosAccess, loading: loadingFeatures } = useSubscriptionFeatures();
  const location = useLocation();
  const navigate = useNavigate();
  const [citas, setCitas] = useState([]);
  const [centerDate, setCenterDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState('');
  const [fadeIn, setFadeIn] = useState(false);
  const [citasStats, setCitasStats] = useState({
    total: 0,
    programadas: 0,
    confirmadas: 0,
    completadas: 0,
    canceladas: 0
  });
  const [enviandoRecordatorio, setEnviandoRecordatorio] = useState(null);
  const calendarRef = useRef(null);

  // Evitar llamadas duplicadas
  const isFetchingRef = useRef(false);

  // Cache de pacientes para resolver nombres una sola vez
  const patientsMapRef = useRef({});
  const patientsLoadedRef = useRef(false);

  const [currentViewMonth, setCurrentViewMonth] = useState(new Date().getMonth() + 1);
  const [currentViewYear, setCurrentViewYear] = useState(new Date().getFullYear());
  const [manualMonth, setManualMonth] = useState(currentViewMonth);
  const [manualYear, setManualYear] = useState(currentViewYear);
  const [isSelectDateDialogOpen, setIsSelectDateDialogOpen] = useState(false);

  const mesesEnEspanol = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showTooltip, setShowTooltip] = useState(true);
  useEffect(() => {
    try {
      const seen = sessionStorage.getItem('citas_tooltip_shown');
      setShowTooltip(seen !== '1'); // si ya se mostró en esta sesión, no mostrar
    } catch {
      // si falla sessionStorage (modo privado muy estricto), deja el valor por defecto
    }    
  }, []);
  
  const hideTooltip = useCallback(() => {
    setShowTooltip(false);
    try {
      sessionStorage.setItem('citas_tooltip_shown', '1');
    } catch {}
  }, []);

  const formatChipDate = useCallback((d) => {
    try {
      return d.toLocaleDateString('es-ES', {
        weekday: 'short', day: '2-digit', month: 'short'
      }).replace(/\.$/, ''); // quita puntos de abreviaturas en algunos navegadores
    } catch {
      return '';
    }
  }, []);

  const updateStatsForCurrentView = useCallback((month = currentViewMonth, year = currentViewYear) => {
    if (!citas.length) return;

    const citasDelMesActual = citas.filter(cita => {
      const fechaCita = new Date(cita.fecha_hora_cita);
      return (
        fechaCita.getMonth() + 1 === month &&
        fechaCita.getFullYear() === year &&
        cita.estado_cita !== 'Cancelada'
      );
    });

    const stats = {
      total: citasDelMesActual.length,
      programadas: citasDelMesActual.filter(cita => cita.estado_cita === 'Programada').length,
      confirmadas: citasDelMesActual.filter(cita => cita.estado_cita === 'Confirmada').length,
      completadas: citasDelMesActual.filter(cita => cita.estado_cita === 'Completada').length,
      canceladas: citas.filter(cita => {
        const fechaCita = new Date(cita.fecha_hora_cita);
        return (
          fechaCita.getMonth() + 1 === month &&
          fechaCita.getFullYear() === year &&
          cita.estado_cita === 'Cancelada'
        );
      }).length
    };

    setCitasStats(stats);
  }, [citas, currentViewMonth, currentViewYear]);

  useEffect(() => {
    updateStatsForCurrentView();
  }, [currentViewMonth, currentViewYear, citas, updateStatsForCurrentView]);

  // Carga de pacientes una sola vez (para nombres)
  const loadPatientsOnce = useCallback(async () => {
    if (patientsLoadedRef.current) return;
    try {
      let url = '/pacientes/?page_size=1000';
      let results = [];
      while (url) {
        const res = await axiosInstance.get(url);
        const data = res.data;
        const pageResults = data.results || data;
        results = results.concat(pageResults);
        if (data.next) {
          try {
            const nextUrl = new URL(
              data.next,
              axiosInstance.defaults.baseURL || window.location.origin
            );
            let rel = nextUrl.pathname + nextUrl.search;
            // Si el backend devuelve '/api/...', quítalo para usar con axiosInstance
            if (rel.startsWith('/api/')) rel = rel.slice(4);
            url = rel;
          } catch {
            url = null;
          }  
        } else { 
          url = null;
        }  
      }
      const map = {};
      results.forEach(p => {
        map[p.id_paciente] = `${p.nombres || ''} ${p.apellidos || ''}`.trim();
      });
      patientsMapRef.current = map;
      patientsLoadedRef.current = true;
    } catch (e) {
      console.warn('No se pudo precargar pacientes:', e);
    }
  }, []);

  const fetchCitas = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      setLoading(true);
      setError(null);

      // Carga de pacientes (para resolver nombres) una sola vez
      await loadPatientsOnce();

      // El backend ya filtra por el usuario autenticado
      const response = await axiosInstance.get('/citas/', { params: { t: Date.now() } });

      const raw = response.data.results || response.data;
      const unicas = Array.isArray(raw)
        ? raw.filter((cita, index, self) =>
            cita && cita.id_cita && index === self.findIndex(c => c.id_cita === cita.id_cita)
          )
        : [];

      // Enriquecer con nombre de paciente y título para el calendario
      const enriquecidas = unicas.map(c => {
        const nombrePac =
          [c.paciente_nombres, c.paciente_apellidos].filter(Boolean).join(' ').trim()
          || c.paciente_nombre
          || (patientsMapRef.current?.[c.paciente_id] || '')
          || (typeof c.paciente === 'string' ? c.paciente : '');

        const title = nombrePac
          ? `${nombrePac}${c.motivo_cita ? ' - ' + c.motivo_cita : ''}`
          : (c.motivo_cita || `Cita #${c.id_cita}`);

        return { ...c, paciente_nombre: nombrePac, title };
      });  
      
      setCitas(enriquecidas);

      // Recalcular estadísticas para el mes/año actualmente visibles
      const mes = currentViewMonth;
      const anio = currentViewYear;

      const citasMes = enriquecidas.filter(c => {
        const d = new Date(c.fecha_hora_cita);
        return d.getMonth() + 1 === mes && d.getFullYear() === anio && c.estado_cita !== 'Cancelada';
      });

      setCitasStats({
        total: citasMes.length,
        programadas: citasMes.filter(c => c.estado_cita === 'Programada').length,
        confirmadas: citasMes.filter(c => c.estado_cita === 'Confirmada').length,
        completadas: citasMes.filter(c => c.estado_cita === 'Completada').length,
        canceladas: enriquecidas.filter(c => {
          const d = new Date(c.fecha_hora_cita);
          return d.getMonth() + 1 === mes && d.getFullYear() === anio && c.estado_cita === 'Cancelada';
        }).length,
      });

      setTimeout(() => setFadeIn(true), 100);
    } catch (err) {
      // Ignora cancelaciones si decides usar AbortController más adelante
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;

      console.error('Error al obtener citas:', err);
      setError('Error al cargar las citas');

      if (err.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      } else {
        toast.error('Error al cargar las citas');
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [currentViewMonth, currentViewYear, loadPatientsOnce]);

  const openSelectDateDialog = () => setIsSelectDateDialogOpen(true);
  const closeSelectDateDialog = () => setIsSelectDateDialogOpen(false);

  const updateStatsManually = () => {
    try {
      const month = manualMonth;
      const year = manualYear;

      setCurrentViewMonth(month);
      setCurrentViewYear(year);

      const citasDelMesActual = citas.filter(cita => {
        const fechaCita = new Date(cita.fecha_hora_cita);
        return (
          fechaCita.getMonth() + 1 === month &&
          fechaCita.getFullYear() === year &&
          cita.estado_cita !== 'Cancelada'
        );
      });

      const stats = {
        total: citasDelMesActual.length,
        programadas: citasDelMesActual.filter(cita => cita.estado_cita === 'Programada').length,
        confirmadas: citasDelMesActual.filter(cita => cita.estado_cita === 'Confirmada').length,
        completadas: citasDelMesActual.filter(cita => cita.estado_cita === 'Completada').length,
        canceladas: citas.filter(cita => {
          const fechaCita = new Date(cita.fecha_hora_cita);
          return (
            fechaCita.getMonth() + 1 === month &&
            fechaCita.getFullYear() === year &&
            cita.estado_cita === 'Cancelada'
          );
        }).length
      };

      setCitasStats(stats);
      toast.success(`Estadísticas actualizadas para ${mesesEnEspanol[month - 1]} de ${year}`);
      closeSelectDateDialog();
    } catch (error) {
      console.error("Error al actualizar estadísticas:", error);
      toast.error("No se pudieron actualizar las estadísticas");
    }
  };

  useEffect(() => {
    if (citas.length > 0) updateStatsForCurrentView();
  }, [citas, updateStatsForCurrentView]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchCitas();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchCitas]);

  const enviarRecordatorio = async (citaId) => {
    if (!hasRecordatoriosAccess) {
      toast.error("No tienes permiso para realizar esta acción.");
      return;
    }
    try {
      setEnviandoRecordatorio(citaId);
      await axiosInstance.post(`/citas/${citaId}/enviar-recordatorio/`, {});
      toast.success('Recordatorio enviado correctamente');
    } catch (error) {
      console.error('Error al enviar recordatorio:', error);
      toast.error(error.response?.data?.error || 'Error al enviar el recordatorio');
    } finally {
      setEnviandoRecordatorio(null);
    }
  };

  useEffect(() => {
    fetchCitas();
  }, [fetchCitas]);

  useEffect(() => {
    if (location.state?.fromAddPaciente && location.state?.pacienteCreadoId) {
      setIsModalOpen(true);
      setPacienteSeleccionado(location.state.pacienteCreadoId);
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.openAddCitaModal) {
      setIsModalOpen(true);
      setPacienteSeleccionado(location.state.pacienteSeleccionadoId || '');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Programada': return 'info';
      case 'Confirmada': return 'success';
      case 'Cancelada': return 'error';
      case 'Completada': return 'secondary';
      default: return 'default';
    }
  };

  // APLICA opciones responsivas al calendario (requiere que CitaCalendar reenvíe ref a FullCalendar)
  const applyCalendarResponsiveOptions = useCallback(() => {
    const api = calendarRef.current?.getApi?.();
    if (!api) return;
    if (isMobile) {
      api.setOption('initialView', 'timeGridDay');
      api.setOption('headerToolbar', { start: 'prev,next today', center: 'title', end: '' });
      api.setOption('dayMaxEventRows', 3);
      api.setOption('moreLinkClick', 'popover');
      api.setOption('expandRows', true);
      api.setOption('height', '100%');
      api.setOption('slotMinTime', '07:00:00');
      api.setOption('slotMaxTime', '21:00:00');
      api.setOption('nowIndicator', true);
      // Tip: si usas locales, define buttonText en CitaCalendar; aquí ajustamos lo mínimo
    } else {
      api.setOption('initialView', 'timeGridWeek');
      api.setOption('headerToolbar', { start: 'prev,next today', center: 'title', end: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek' });
      api.setOption('expandRows', true);
      api.setOption('height', '100%');
      api.setOption('dayMaxEventRows', false);
    }
    try {
      setCenterDate(api.getDate());
    } catch {}
    // tras cambio de opciones, asegura recalcular tamaño
    setTimeout(() => api.updateSize(), 50);
  }, [isMobile]);

  useEffect(() => {
    applyCalendarResponsiveOptions();
  }, [applyCalendarResponsiveOptions]);

  // Acciones rápidas de vista para móviles
  const changeCalendarView = (viewName) => {
    const api = calendarRef.current?.getApi?.();
    if (!api) return;
    api.changeView(viewName);
    try {
      setCenterDate(api.getDate());
    } catch {}
    setTimeout(() => api.updateSize(), 30);
  };

  const goToToday = () => {
    const api = calendarRef.current?.getApi?.();
    if (!api) return;
    api.today();
    try {
      setCenterDate(api.getDate());
      api.scrollToTime?.('09:00:00');
    } catch {}
  };


  const CitaCalendarWithReminder = ({ citas, onListChange }) => {
    const citasArray = Array.isArray(citas) ? citas : [];
    const handleViewChange = (viewInfo) => {
      const viewDate = viewInfo.view.currentStart;
      const month = viewDate.getMonth() + 1;
      const year = viewDate.getFullYear();
      setCurrentViewMonth(month);
      setCurrentViewYear(year);
      setManualMonth(month);
      setManualYear(year);
      setCenterDate(viewDate);
    };

    return (
      <CitaCalendar
        citas={citasArray}
        onListChange={onListChange}
        onSendReminder={enviarRecordatorio}
        sendingReminder={enviandoRecordatorio}
        showReminderButton={hasRecordatoriosAccess}
        onViewChange={handleViewChange}
        calendarRef={calendarRef}
      />
    );
  };

  if (loading || loadingFeatures) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center',
        p: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Cargando calendario...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        height: '100%', 
        p: 4, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <Avatar sx={{ bgcolor: 'error.main', width: 60, height: 60, mb: 2 }}>
          <CloseIcon fontSize="large" />
        </Avatar>
        <Typography variant="h5" gutterBottom>
          No se pudieron cargar las citas
        </Typography>
        <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchCitas}
          startIcon={<RefreshIcon />}
          sx={{
            borderRadius: '20px',
            py: 1.2,
            px: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            }
          }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  const MotionPaper = motion(Paper);
  const isFemale = user?.medico?.genero === 'Femenino';
  const prefijo = isFemale ? 'Dra.' : 'Dr.';
  const nombreCompleto = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || 'Médico';


  return (
    <Box 
      sx={{ 
        p: { xs: 1, sm: 2 }, 
        width: '100%',
        maxWidth: '100%',
        m: 0, 
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Fade in={fadeIn} timeout={800}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* Cabecera: Título y Botón */}
          <MotionPaper
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 4,
              mb: isMobile ? 2 : 4,
              px: { xs: 3, md: 5 },
              py: { xs: 3, md: 4 },
              background: (t) =>
                `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
              color: 'white',
            }}
          >
            {/* Burbujas decorativas */}
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: (t) => alpha('#fff', 0.1),
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: (t) => alpha('#fff', 0.08),
              }}
            />

            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
              }}
            >
              {/* Izquierda: icono + textos */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: (t) => alpha('#fff', 0.2),
                    color: 'white',
                    borderRadius: '50%',
                    width: 60,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: (t) => `0 8px 32px ${alpha('#000', 0.2)}`,
                    flexShrink: 0,
                  }}
                >
                  <CalendarMonthIcon sx={{ fontSize: 34 }} />
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, textShadow: '0 2px 10px rgba(0,0,0,0.25)' }}
                  >
                    Calendario de Citas
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {`${prefijo} ${nombreCompleto}`}
                  </Typography>
                </Box>
              </Box>

              {/* Derecha: tus botones existentes, sin cambios de lógica */}
              <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
                {hasRecordatoriosAccess && (
                  <Button 
                    variant="outlined" 
                    startIcon={<EmailIcon />}
                    onClick={() => navigate('/configuracion-recordatorios')} 
                    sx={{
                      borderRadius: '20px',
                      py: isMobile ? 0.8 : 1.2,
                      px: isMobile ? 1 : 3,
                      transition: 'all 0.2s ease',
                      fontSize: isMobile ? '0.75rem' : 'inherit',
                      bgcolor: alpha('#fff', 0.08),
                      borderColor: alpha('#fff', 0.3),
                      color: 'white',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        bgcolor: alpha('#fff', 0.15),
                        borderColor: alpha('#fff', 0.5),
                      }
                    }}
                  >
                    {isMobile ? 'Recordatorios' : 'Configurar Recordatorios'}
                  </Button>
                )}

                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => { setIsModalOpen(true); setPacienteSeleccionado(''); }} 
                  disabled={loading}
                  sx={{
                    borderRadius: '20px',
                    py: isMobile ? 0.8 : 1.2,
                    px: isMobile ? 1 : 3,
                    transition: 'all 0.2s ease',
                    fontSize: isMobile ? '0.75rem' : 'inherit',
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      bgcolor: 'grey.100',
                      boxShadow: '0 6px 25px rgba(0,0,0,0.2)',
                    }
                  }}
                >
                  {isMobile ? 'Programar' : 'Programar Nueva Cita'}
                </Button>
              </Box>
            </Box>
          </MotionPaper>

          <Typography 
            variant="subtitle1" 
            color="text.secondary" 
            sx={{ mb: 1, fontWeight: 'medium' }}
          >
            Estadísticas para {`${mesesEnEspanol[currentViewMonth - 1]} de ${currentViewYear}`}
          </Typography>

          {/* Tarjetas de estadísticas */}
          <Box sx={{ mb: isMobile ? 2 : 4 }}>
            <Grid container spacing={isMobile ? 1 : 2}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[1],
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-5px)',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.background.paper, 0.8) 
                        : alpha(theme.palette.background.paper, 0.9),
                    }
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center', 
                    p: isMobile ? 1 : 2, 
                  }}>
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      fontWeight="bold" 
                      color="text.primary"
                    >
                      {citasStats.total}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : 'inherit' }}
                    >
                      Total de Citas
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={2.4}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[1],
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.2)}`,
                      transform: 'translateY(-5px)',
                      backgroundColor: alpha(theme.palette.info.main, 0.05),
                    }
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center', 
                    p: isMobile ? 1 : 2, 
                  }}>
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      fontWeight="bold" 
                      color="info.main"
                    >
                      {citasStats.programadas}
                    </Typography>
                    <Chip 
                      label="Programadas" 
                      color="info" 
                      size="small" 
                      sx={{ 
                        mt: 0.5,
                        transition: 'all 0.3s ease',
                        ...(isMobile && {
                          height: '20px',
                          '& .MuiChip-label': {
                            fontSize: '0.6rem',
                            padding: '0 6px',
                          }
                        })
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={2.4}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[1],
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.2)}`,
                      transform: 'translateY(-5px)',
                      backgroundColor: alpha(theme.palette.success.main, 0.05),
                    }
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center', 
                    p: isMobile ? 1 : 2, 
                  }}>
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      fontWeight="bold" 
                      color="success.main"
                    >
                      {citasStats.confirmadas}
                    </Typography>
                    <Chip 
                      label="Confirmadas" 
                      color="success" 
                      size="small" 
                      sx={{ 
                        mt: 0.5,
                        transition: 'all 0.3s ease',
                        ...(isMobile && {
                          height: '20px',
                          '& .MuiChip-label': {
                            fontSize: '0.6rem',
                            padding: '0 6px',
                          }
                        })
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={2.4}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[1],
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: `0 8px 16px ${alpha(theme.palette.secondary.main, 0.2)}`,
                      transform: 'translateY(-5px)',
                      backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                    }
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center', 
                    p: isMobile ? 1 : 2, 
                  }}>
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      fontWeight="bold" 
                      color="secondary.main"
                    >
                      {citasStats.completadas}
                    </Typography>
                    <Chip 
                      label="Completadas" 
                      color="secondary" 
                      size="small" 
                      sx={{ 
                        mt: 0.5,
                        transition: 'all 0.3s ease',
                        ...(isMobile && {
                          height: '20px',
                          '& .MuiChip-label': {
                            fontSize: '0.6rem',
                            padding: '0 6px',
                          }
                        })
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={6} md={2.4}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[1],
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: `0 8px 16px ${alpha(theme.palette.error.main, 0.2)}`,
                      transform: 'translateY(-5px)',
                      backgroundColor: alpha(theme.palette.error.main, 0.05),
                    }
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center', 
                    p: isMobile ? 1 : 2, 
                  }}>
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      fontWeight="bold" 
                      color="error.main"
                    >
                      {citasStats.canceladas}
                    </Typography>
                    <Chip 
                      label="Canceladas" 
                      color="error" 
                      size="small" 
                      sx={{ 
                        mt: 0.5,
                        transition: 'all 0.3s ease',
                        ...(isMobile && {
                          height: '20px',
                          '& .MuiChip-label': {
                            fontSize: '0.6rem',
                            padding: '0 6px',
                          }
                        })
                      }}
                    />
                  </CardContent>
                </Card>                
              </Grid>
              {/* Botón de actualización como un elemento más pequeño */}
              <Grid item xs={6} sm={6} md={2.4}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      borderRadius: theme.shape.borderRadius,
                      boxShadow: theme.shadows[1],
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      backgroundColor: '#4fc3f7',
                      height: '100%', 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        boxShadow: `0 8px 16px ${alpha('#4fc3f7', 0.4)}`,
                        transform: 'translateY(-5px)',
                        backgroundColor: '#03a9f4',
                      }
                    }}
                    onClick={openSelectDateDialog}
                  >
                    <CardContent sx={{ 
                      textAlign: 'center', 
                      p: isMobile ? 1 : 2,
                      color: 'white',
                      backgroundColor: '#7FAAD3',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <RefreshIcon sx={{ fontSize: isMobile ? '1.5rem' : '2rem', mb: 0.5 }} />
                      <Typography 
                        variant="body2"
                        fontWeight="bold"
                      >
                        {isMobile ? 'Actualizar' : 'Actualizar Estadísticas'}
                      </Typography>
                    </CardContent>
                  </Card>
              </Grid>
            </Grid> 
          </Box>

          {/* Tip móvil */}
          {showTooltip && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: '80px',
              right: '20px',
              zIndex: 1000,
              padding: '12px 16px',
              borderRadius: '16px',
              backgroundColor: alpha(theme.palette.info.main, 0.95),
              color: 'white',
              maxWidth: '300px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TouchAppIcon />
                <Typography variant="subtitle1" fontWeight="bold">
                  ¡Consejo útil!
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={hideTooltip}
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2">
              Puedes arrastrar y soltar las citas en el calendario para cambiar su horario.
            </Typography>
            <Typography variant="body2">
              También puedes ajustar la duración arrastrando el borde inferior de cada cita.
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={hideTooltip}
              sx={{ 
                mt: 1, 
                color: 'white', 
                borderColor: 'white',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderColor: 'white'
                }
              }}
            >
              Entendido
            </Button>
          </Paper>
        )}

      

          {/* Calendario */}
          <Paper 
            elevation={0}
            variant="outlined"
            sx={{ 
              flex: 1,
              minHeight: 0,
              width: '100%',
              maxWidth: '100%',
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: theme.shadows[1],
              ...(isMobile && {
                mx: -1, // un pequeño ajuste para aprovechar ancho sin recortar sombras
                borderRadius: 0,
                borderLeft: 'none',
                borderRight: 'none',
              }),
              // Estilos de FullCalendar para móviles
              '& .fc': {
                fontSize: isMobile ? '0.85rem' : '1rem'
              },
              '& .fc .fc-toolbar-title': {
                fontSize: isMobile ? '1rem' : '1.25rem'
              },
              '& .fc .fc-button': {
                padding: isMobile ? '4px 8px' : undefined,
                fontSize: isMobile ? '0.75rem' : undefined,
                borderRadius: '16px'
              },
              '& .fc .fc-timegrid-slot': {
                height: isMobile ? '40px' : '52px'
              },
              '& .fc .fc-timegrid-axis-cushion, & .fc .fc-timegrid-slot-label-cushion': {
                fontSize: isMobile ? '0.7rem' : undefined,
                padding: isMobile ? '2px' : undefined
              },
              '& .fc .fc-daygrid-event, & .fc .fc-timegrid-event': {
                borderRadius: '10px'
              },
              '& .fc .fc-daygrid-event .fc-event-time, & .fc .fc-daygrid-event .fc-event-title': {
                whiteSpace: 'nowrap'
              }
            }}
          >
            <Box sx={{ 
              flex: '1 1 auto',   
              minHeight: 0, 
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              ...(isMobile && {
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
              })
            }}>
              <CitaCalendarWithReminder citas={citas} onListChange={fetchCitas} />
            </Box>
          </Paper>
        </Box>
      </Fade>

      {/* Diálogo para añadir cita */}
      <Dialog 
        open={isModalOpen} 
        onClose={() => {
          setIsModalOpen(false);
          setPacienteSeleccionado('');
        }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile} // Pantalla completa en móviles
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '28px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
        component="div"
        sx={{ 
          backgroundColor: 'primary.light', 
          color: 'primary.contrastText',
          py: isMobile ? 1.5 : 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EventNoteIcon sx={{ mr: 1.5 }} />
            <Typography variant={isMobile ? "subtitle1" : "h6"}>
              Programar Nueva Cita
            </Typography>
          </Box>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={() => {
              setIsModalOpen(false);
              setPacienteSeleccionado('');
            }} 
            aria-label="close"
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: isMobile ? 2 : 3,
          mt: isMobile ? 0.5 : 1
        }}>
          <AddCita 
            onListChange={fetchCitas} 
            onClose={() => {
              setIsModalOpen(false);
              setPacienteSeleccionado('');
            }}
            pacienteSeleccionado={pacienteSeleccionado}
          />
        </DialogContent>
      </Dialog>

      {/* Diálogo para seleccionar mes y año */}
      <Dialog
        open={isSelectDateDialogOpen}
        onClose={closeSelectDateDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Seleccionar mes y año
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Mes</InputLabel>
              <Select
                value={manualMonth}
                onChange={(e) => setManualMonth(e.target.value)}
                label="Mes"
              >
                {mesesEnEspanol.map((mes, index) => (
                  <MenuItem key={index} value={index + 1}>
                    {mes}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Año</InputLabel>
              <Select
                value={manualYear}
                onChange={(e) => setManualYear(e.target.value)}
                label="Año"
              >
                {[2024, 2025, 2026].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={closeSelectDateDialog} color="inherit">
              Cancelar
            </Button>
            <Button 
              variant="contained"
              onClick={updateStatsManually}
            >
              Actualizar Estadísticas
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default CitasPage;
